import { Ionicons } from '@expo/vector-icons';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { ThemedText } from '../themed-text';

type DateRangePickerProps = {
  startDate: Date;
  endDate: Date;
  onChange: (range: { start: Date; end: Date }) => void;
  /** Posição do modal: 'center' (padrão) ou 'top'. Padrão: center. */
  dropdownPlacement?: 'top' | 'center';
};

const WEEK_DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  dropdownPlacement = 'center',
}: DateRangePickerProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Local state for temporary selection before confirming
  const [tempStart, setTempStart] = useState<Date>(startDate);
  const [tempEnd, setTempEnd] = useState<Date>(endDate);
  const [selectingEnd, setSelectingEnd] = useState(false);

  const openModal = () => {
    setTempStart(startDate);
    setTempEnd(endDate);
    setCurrentMonth(startDate);
    void KeyboardController.dismiss();
    setModalVisible(true);
    setSelectingEnd(false); // Reset selection mode
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const confirmSelection = () => {
    // Ensure start is before end
    let finalStart = tempStart;
    let finalEnd = tempEnd;

    if (finalStart > finalEnd) {
      finalStart = tempEnd;
      finalEnd = tempStart;
    }

    onChange({ start: finalStart, end: finalEnd });
    closeModal();
  };

  const handleDayPress = (day: Date) => {
    if (!selectingEnd) {
      // First click: selecting start
      setTempStart(day);
      setTempEnd(day); // Reset end to start initially
      setSelectingEnd(true);
    } else {
      // Second click: selecting end
      let finalStart = tempStart;
      let finalEnd = day;

      if (day < tempStart) {
        finalStart = day;
        finalEnd = tempStart;
      }

      setTempStart(finalStart);
      setTempEnd(finalEnd);
      setSelectingEnd(false); // Finished selection cycle, next click restarts
    }
  };

  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Padding days for grid alignment
  const startDayOfWeek = startOfMonth(currentMonth).getDay(); // 0 (Sun) - 6 (Sat)
  const emptyDays = Array.from({ length: startDayOfWeek });

  return (
    <View>
      <Pressable
        onPress={openModal}
        className="flex-row items-center justify-between rounded-lg border border-input bg-background px-3 py-3"
      >
        <ThemedText className="font-medium text-base text-foreground">
          {format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')}
        </ThemedText>
        <Ionicons name="calendar-outline" size={20} className="text-muted-foreground" />
      </Pressable>

      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        onRequestClose={closeModal}
      >
        <View className="flex-1 justify-center bg-black/50 p-4">
          <View className="w-full max-w-md self-center rounded-xl bg-card p-6 shadow-lg">
            {/* Modal Header */}
            <View className="mb-4 flex-row items-center justify-between border-b border-border pb-4">
              <ThemedText className="font-semibold text-lg">Selecionar Período</ThemedText>
              <Pressable onPress={closeModal}>
                <Ionicons name="close" size={24} className="text-muted-foreground" />
              </Pressable>
            </View>

            {/* Calendar Controls */}
            <View className="mb-4 flex-row items-center justify-between">
              <Pressable onPress={previousMonth} className="p-2">
                <Ionicons name="chevron-back" size={20} className="text-primary" />
              </Pressable>
              <ThemedText className="font-medium text-base capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
              </ThemedText>
              <Pressable onPress={nextMonth} className="p-2">
                <Ionicons name="chevron-forward" size={20} className="text-primary" />
              </Pressable>
            </View>

            {/* Week Days Header */}
            <View className="mb-2 flex-row">
              {WEEK_DAYS.map((day, index) => (
                <View key={index} className="flex-1 items-center">
                  <ThemedText className="font-medium text-xs text-muted-foreground">
                    {day}
                  </ThemedText>
                </View>
              ))}
            </View>

            {/* Custom Month Grid */}
            <View className="flex-row flex-wrap">
              {emptyDays.map((_, index) => (
                <View key={`empty-${index}`} className="aspect-square w-[14.28%]" />
              ))}
              {daysInMonth.map((day) => {
                const isStart = isSameDay(day, tempStart);
                const isEnd = isSameDay(day, tempEnd);
                const isBetween = isWithinInterval(day, {
                  start: tempStart < tempEnd ? tempStart : tempEnd,
                  end: tempEnd > tempStart ? tempEnd : tempStart,
                });

                // Styling Logic
                const isSelected = isStart || isEnd;
                const isRange = isBetween && !isSelected;

                return (
                  <Pressable
                    key={day.toISOString()}
                    onPress={() => handleDayPress(day)}
                    className="aspect-square w-[14.28%] items-center justify-center p-[2px]"
                  >
                    <View
                      className={`
                        h-full w-full items-center justify-center rounded-full
                        ${isSelected ? 'bg-primary' : ''}
                        ${isRange ? 'rounded-none bg-primary/20' : ''}
                        ${isStart && isBetween && !isSameDay(tempStart, tempEnd) ? 'rounded-l-full rounded-r-none' : ''}
                        ${isEnd && isBetween && !isSameDay(tempStart, tempEnd) ? 'rounded-l-none rounded-r-full' : ''}
                      `}
                    >
                      <ThemedText
                        className={`text-sm ${
                          isSelected ? 'font-semibold text-primary-foreground' : 'text-foreground'
                        }`}
                      >
                        {format(day, 'd')}
                      </ThemedText>
                    </View>
                  </Pressable>
                );
              })}
            </View>

            {/* Footer Actions */}
            <View className="mt-6 flex-row justify-end gap-3 border-t border-border pt-4">
              <Pressable onPress={closeModal} className="px-4 py-2">
                <ThemedText className="text-foreground">Cancelar</ThemedText>
              </Pressable>
              <Pressable
                onPress={confirmSelection}
                className="items-center justify-center rounded-lg bg-primary px-4 py-2"
              >
                <ThemedText className="font-semibold text-primary-foreground">Confirmar</ThemedText>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
